/**
 * Genre data structures for story generation prompt assembly.
 *
 * All genre-specific rules, structures, age adaptations, and character guidance
 * are defined here as typed data. The prompt builder selects and composes
 * the relevant pieces — no variability or conditionals reach the final prompt.
 */

// ─── Types ──────────────────────────────────────────────────────────

export interface GenreBeat {
  name: string;
  percentage: number;
  description: string;
}

export interface GenreDefinition {
  /** Display name */
  name: string;
  /** System prompt for LLM */
  systemPrompt: string;
  /** Structural beats */
  beats: GenreBeat[];
  /** Mandatory rules */
  rules: string[];
  /** Things the LLM must NOT do */
  prohibitions: string[];
  /** Style guidance */
  style: {
    dialogCount: string;
    pacing: string;
    extra: string[];
  };
}

export type AgeGroup = '1-3' | '3-5' | '5-7' | '7-10' | '10+';

// ─── Genre Definitions ─────────────────────────────────────────────

const GENRES: Record<string, GenreDefinition> = {
  adventure: {
    name: 'Приключение',
    systemPrompt: 'Ты — мастер детских приключений. Твоя аудитория ждёт динамики, волшебства и ощущения, что даже маленький ребёнок может совершить большое дело. Создаёшь истории с путешествиями, уникальными локациями и испытаниями, где герой побеждает смекалкой. В центре — ясный квест с понятным Макгаффином.',
    beats: [
      { name: 'Обычный мир', percentage: 10, description: 'Герой в знакомом, безопасном месте. Покажи его мечту, нужду или особенность.' },
      { name: 'Зов к приключению', percentage: 5, description: 'Событие, которое невозможно игнорировать: послание, просьба о помощи, таинственная находка.' },
      { name: 'Наставник и порог', percentage: 5, description: 'Мудрый персонаж направляет или герой решается сам. Первый совет или предмет. Переход в неизвестное.' },
      { name: 'Три локации с испытаниями', percentage: 40, description: '3 УНИКАЛЬНЫЕ локации. В каждой: новое место с атмосферой, физическое перемещение, испытание на конкретный навык, союзник или артефакт для кульминации. Каждое испытание сложнее предыдущего.' },
      { name: 'Кульминация', percentage: 15, description: 'Самый опасный момент. Герой использует ВСЕ собранные ресурсы. Каждый найденный предмет/навык ДОЛЖЕН сыграть роль.' },
      { name: 'Награда и возвращение', percentage: 15, description: 'Победа, мир изменился. Герой вырос. Возвращение домой — герой уже другой.' },
      { name: 'Новая жизнь', percentage: 10, description: 'Короткий яркий финал. Намёк на продолжение. Тёплое ощущение.' },
    ],
    rules: [
      'Артефакт/навык из одного испытания ОБЯЗАТЕЛЬНО используется в кульминации',
      'Союзник, найденный в пути, помогает в финале',
      'Никаких случайных спасений — всё заслужено',
      'Каждая локация УНИКАЛЬНА (не "ещё один лес")',
      'Описывай физическое перемещение между локациями: тропа, переправа, подъём',
      'Show, Don\'t Tell: показывай характер через действия, а не через описания',
    ],
    prohibitions: [],
    style: {
      dialogCount: '5-7',
      pacing: 'Действие: короткие, рубленые фразы ("Прыгнул. Схватился. Удержался!"). Паузы: длинные описательные предложения. Чередуй действие и передышки.',
      extra: ['У каждого места свои звуки, запахи, цвета', 'Сенсорная плотность: яркие цвета, запахи, ощущение ветра'],
    },
  },

  mystery: {
    name: 'Детектив / загадка',
    systemPrompt: 'Ты — профессиональный автор детских детективов. Твоя аудитория умна и любит разгадывать загадки — не говори с ними свысока. Создаёшь истории-расследования с уликами, подозреваемыми, ложными следами и логической разгадкой, которую читатель может найти сам.',
    beats: [
      { name: 'Загадка', percentage: 10, description: 'Обнаруживается проблема: что-то пропало, произошло нечто странное. Загадка конкретная, понятная ребёнку.' },
      { name: 'Первые улики', percentage: 15, description: 'Герой осматривает место, находит 2-3 ФИЗИЧЕСКИЕ подсказки (крошки, следы, пятна). Рассуждает ВСЛУХ.' },
      { name: 'Подозреваемые', percentage: 20, description: '2-3 персонажа, каждый рассказывает свою версию. Одна деталь кажется не важной, но окажется ключевой.' },
      { name: 'Ложный след', percentage: 15, description: 'Кажется, ответ найден! Но одна деталь противоречит версии. "Подожди... Но если это был он, то почему...?"' },
      { name: 'Ключевая улика', percentage: 15, description: 'Деталь, которая меняет всё. Эта деталь УЖЕ БЫЛА в тексте ранее (fair play). Момент озарения.' },
      { name: 'Разгадка', percentage: 15, description: 'Герой объясняет решение, перечисляет улики. Настоящая причина — бытовая и невинная.' },
      { name: 'Финал', percentage: 10, description: 'Справедливость восстановлена, никто не наказан жёстко, лёгкий юмор.' },
    ],
    rules: [
      'ВСЕ улики ОБЯЗАТЕЛЬНО появляются в тексте ДО разгадки (fair play)',
      'Ребёнок-слушатель должен иметь возможность догадаться сам',
      'Ложный след логичный, но опровергаемый одной деталью',
      'Герой ВСЕГДА проговаривает мысли: "Значит, если здесь были крошки, то..."',
      'Используй: "Хм...", "Интересно...", "А что если...", "Подождите-ка..."',
      'Внедри "тикающие часы" — дедлайн, до которого нужно найти разгадку (до фестиваля, до прихода родителей)',
      'Show, Don\'t Tell: показывай подозрительное поведение через скрещенные руки, бегающий взгляд, спотыкающуюся речь',
    ],
    prohibitions: [
      'Настоящие преступления, воровство со злым умыслом',
      'Страшные или жуткие элементы',
      'Жёсткое наказание "виновного"',
      'Разгадка без предварительных улик',
      'Deus ex machina (герой не может "просто угадать")',
    ],
    style: {
      dialogCount: '8-10',
      pacing: 'Допросы и расспросы: короткие вопросы, неожиданные ответы. Каждый свидетель говорит по-своему.',
      extra: ['Любопытство, а не страх', 'Азарт расследования, а не тревога', 'Лёгкий юмор разбавляет напряжение'],
    },
  },

  comedy: {
    name: 'Комедия / небылица',
    systemPrompt: 'Ты — гениальный детский писатель-юморист. Твои тексты вызывают у детей смех вслух. Создаёшь абсурдные, нелепые ситуации без грубости, используя правило трёх, эскалацию абсурда и обман ожиданий. Без морали и нравоучений.',
    beats: [
      { name: 'Герой и его "особенность"', percentage: 10, description: 'У героя забавная черта (чихает — всё разлетается, всё путает, превращает всё в варенье). Покажи сразу в действии.' },
      { name: 'Первая попытка', percentage: 15, description: 'Герой пытается что-то сделать. Его "особенность" мешает. Результат забавный, но терпимый. Звукоподражания: "Бумс!", "Шлёп!"' },
      { name: 'Вторая попытка', percentage: 20, description: 'То же самое, но ХУЖЕ и АБСУРДНЕЕ. Масштаб проблемы растёт. Быстрый обмен репликами.' },
      { name: 'Третья попытка — полный хаос', percentage: 25, description: 'Всё наперекосяк, абсурд на максимуме. "Катастрофа" — СМЕШНАЯ, не страшная. Максимум звукоподражаний.' },
      { name: 'Неожиданный поворот', percentage: 15, description: '"Особенность" героя СЛУЧАЙНО спасает ситуацию нелепым способом. Обман ожидания.' },
      { name: 'Смешной финал', percentage: 15, description: 'Всё хорошо, но с забавным "хвостиком". Последняя шутка — самая сильная.' },
    ],
    rules: [
      'Правило трёх: два раза ожидаемо, третий — абсурд',
      'Эскалация: каждая ситуация абсурднее предыдущей',
      'Обман ожиданий: читатель ждёт одного — получает другое',
      'Минимум 5-6 звукоподражаний: "Бабах!", "Хлюп!", "Дзынь!", "Шмяк!", "Бултых!"',
      'Быстрые диалоги как в мультфильме',
      'Панчлайны (ударные смешные слова) — в конец предложения',
      'Show, Don\'t Tell: показывай комичность через действия и звуки, не объясняй "это было смешно"',
    ],
    prohibitions: [
      'Серьёзный тон, нравоучения, слово "мораль", слово "урок"',
      'Назидательный финал ("и понял он, что надо...")',
      'Грустные или страшные моменты',
      'Оскорбления, буллинг, высмеивание особенностей',
      'Длинные описания без шуток (каждые 2-3 предложения — смешной момент)',
    ],
    style: {
      dialogCount: '8-10',
      pacing: 'Быстрый. Персонажи говорят по-разному: один умничает, другой всё путает.',
      extra: ['Смешные имена, названия, слова', 'Минимум 5 звукоподражаний'],
    },
  },

  lullaby: {
    name: 'Колыбельная история',
    systemPrompt: 'Ты — специалист по детскому сну и автор идеальных колыбельных. Создаёшь спокойные, ритмичные истории с нисходящей дугой энергии, которые физически замедляют темп с каждым абзацем. Абсолютная эмоциональная безопасность — герой окружён безусловной любовью.',
    beats: [
      { name: 'Мягкое начало', percentage: 15, description: 'Тёплый вечер, уютная обстановка. Герой завершает дела. Золотистый закат, спокойный ритм.' },
      { name: 'Путешествие ко сну', percentage: 50, description: 'Медленное движение через уютные локации. Каждая ТИШЕ предыдущей. Повторяющиеся рефрены каждые 2-3 блока. Сенсорные образы: тепло одеяла, мерцание звёзд, шёпот ветра.' },
      { name: 'Замедление', percentage: 20, description: 'Мир засыпает: цветы закрываются, птицы замолкают. Очень длинные, плавные предложения. Повторение успокаивающих фраз из начала.' },
      { name: 'Засыпание', percentage: 15, description: 'Герой находит уютное место. Глаза закрываются, дыхание ровное. Тишина, покой, тепло. Короткие, нежные фразы.' },
    ],
    rules: [
      'Каждый следующий блок СПОКОЙНЕЕ предыдущего — главное правило',
      'Используй повторяющийся рефрен через историю',
      'Ритмичные конструкции: "Тихо-тихо...", "Всё спокойнее...", "Всё теплее..."',
      'Сенсорные образы: тепло, мягкость, мерцание, шёпот, запахи мёда и молока',
      'Уменьшительно-ласкательные формы: звёздочка, облачко, ручеёк',
    ],
    prohibitions: [
      'Конфликты, злодеи, опасности, препятствия',
      'Громкие звуки, бег, крики, споры, драки',
      'Напряжение, интрига, неожиданные повороты',
      'Нравоучения, мораль, уроки',
      'Вопросы к читателю, восклицания',
    ],
    style: {
      dialogCount: '3-4 максимум, только шёпотом',
      pacing: 'Нисходящий. Длинные, плавные предложения с обилием запятых. Много прилагательных: мягкий, тёплый, тихий, нежный, уютный.',
      extra: [
        'Финальные 2-3 блока — почти шёпот',
        'Фонетика: предпочитай шипящие и сонорные (ш, с, л, м, н). Избегай резких согласных звуков',
      ],
    },
  },

  fable: {
    name: 'Притча',
    systemPrompt: 'Ты — современный детский сказочник в традиции Эзопа и Толстого. Создаёшь истории, которые мягко учат важному жизненному навыку через увлекательную метафору и контраст двух путей. Мораль вытекает из действий, не из нотаций.',
    beats: [
      { name: 'Ситуация и персонажи', percentage: 15, description: 'Два персонажа с разными подходами к жизни. Оба хорошие, просто разные. Покажи характеры через действия.' },
      { name: 'Общая ситуация и выбор', percentage: 10, description: 'Оба сталкиваются с ОДНОЙ ситуацией. Каждый выбирает свой путь. Выбор логичен для характера.' },
      { name: 'Путь первого', percentage: 25, description: 'Действия и последствия. Если путь "правильный" — покажи трудности. Если "ошибочный" — покажи привлекательность.' },
      { name: 'Путь второго', percentage: 25, description: 'Параллельная линия. Зеркальная структура: те же этапы, другие решения. Контраст НАГЛЯДНЫЙ.' },
      { name: 'Встреча', percentage: 15, description: 'Пути пересекаются. Контраст результатов виден без слов. Нет злорадства, нет "я же говорил".' },
      { name: 'Осознание и примирение', percentage: 10, description: '"Ошибавшийся" САМИ приходит к пониманию. Никакого наказания. Оба остаются друзьями.' },
    ],
    rules: [
      'Мораль через КОНТРАСТ двух путей, не через слова',
      '"Неправильный" персонаж — НЕ злодей, его мотивы понятны',
      'Ребёнок сам делает вывод, видя контраст',
      'Ёмкий, лаконичный язык (как у Толстого в детских рассказах)',
      'Конкретные детали вместо абстракций',
      'Show, Don\'t Tell: "Муравей нёс зёрнышко по крутой тропинке" — а не "Муравей был трудолюбивым"',
    ],
    prohibitions: [
      'Прямые нравоучения ("запомни, дети, что...")',
      'Наказание "неправильного" персонажа',
      'Идеализация "правильного" персонажа',
      'Взрослый/мудрец, который объясняет мораль',
      'Фраза "и он понял, что..." в лоб',
    ],
    style: {
      dialogCount: '5-7',
      pacing: 'Ёмкий. Короткие, чёткие предложения. "Муравей нёс зёрнышко по крутой тропинке" — а не "Муравей был трудолюбивым".',
      extra: ['Каждый персонаж говорит по-своему'],
    },
  },

  everyday: {
    name: 'Бытовая история',
    systemPrompt: 'Ты — сценарист лучших детских историй в стиле "Bluey". Создаёшь истории, воспевающие красоту обычного дня, детского воображения и семейной теплоты. Без волшебства, только реальные эмоции.',
    beats: [
      { name: 'Обычный день', percentage: 15, description: 'Знакомый мир: дом, двор, садик, школа. Обычные дела. Атмосфера реальности.' },
      { name: 'Ситуация', percentage: 15, description: 'Эмоционально заряженная ситуация: страх, обида, зависть, стеснение, непонимание, потеря.' },
      { name: 'Переживание', percentage: 20, description: 'Герой ЧУВСТВУЕТ. Физические ощущения ("живот сжался"), мысли ("А вдруг..."), поведение. Называй эмоции прямо.' },
      { name: 'Попытка справиться', percentage: 20, description: 'Герой пробует сам. Не всегда успешно. Может ошибиться. Покажи последствия.' },
      { name: 'Помощь / разговор', percentage: 15, description: 'Друг или взрослый помогает ПОНЯТЬ, а не решает за героя. Мягкий совет. Взрослый не идеальный.' },
      { name: 'Новый опыт', percentage: 15, description: 'Герой пробует по-новому. Получается НЕМНОГО лучше — не идеально. Маленькая победа.' },
    ],
    rules: [
      'НАЗЫВАЙ эмоции: "обидно", "страшно", "стыдно", "одиноко"',
      'Описывай ТЕЛЕСНЫЕ ощущения: "сердце забилось быстрее"',
      'Покажи, что чувства героя нормальны',
      'Нет идеальных решений — "стало немного лучше"',
      'Взрослые тоже ошибаются и извиняются',
      'Show, Don\'t Tell: показывай эмоции через телесные ощущения и действия',
    ],
    prohibitions: [
      'Волшебство, говорящие животные, сверхъестественное',
      'Идеальные решения ("и всё стало прекрасно")',
      'Идеальные взрослые (всезнающие, безупречные)',
      'Наказание как способ решения',
      'Обесценивание чувств ("не бойся, тут нечего бояться")',
    ],
    style: {
      dialogCount: '6-8',
      pacing: 'Реалистичные детские диалоги: "Ну и ладно!", "Не хочу!", "А почему?". Короткие предложения в эмоциональных моментах, длинные в спокойных.',
      extra: ['Конкретные узнаваемые детали: садик, качели, раздевалка', 'Фокус на внутреннем мире героя'],
    },
  },

  educational: {
    name: 'Познавательная история',
    systemPrompt: 'Ты — талантливый детский педагог и сказочник, как авторы "Волшебного школьного автобуса". Объясняешь научные факты через увлекательное путешествие героя, а не через лекции. Факты подаёшь дозированно и только через действие.',
    beats: [
      { name: 'Вопрос', percentage: 10, description: 'Герой сталкивается с чем-то непонятным. Конкретный вопрос: "Почему листья желтеют?" Вопрос должен быть мотивирован наблюдением.' },
      { name: 'Начало исследования', percentage: 10, description: 'Герой находит способ исследовать тему. ВАРИАНТЫ (чередуй!): нашёл книгу/карту, встретил проводника, попал в музей/лабораторию, увидел явление вживую, начал эксперимент. НЕ уменьшайся каждый раз.' },
      { name: 'Открытие 1', percentage: 15, description: 'Первый факт ПОКАЗАН через приключение. Герой задаёт вопрос "почему?" и получает ответ с КОНКРЕТНЫМ фактом (название, число).' },
      { name: 'Открытие 2', percentage: 15, description: 'Второй факт, связан с первым. Подаётся ИНАЧЕ: через эксперимент, загадку, сравнение, диалог-спор. Не повторяй формат Открытия 1.' },
      { name: 'Открытие 3', percentage: 10, description: 'Третий факт — неожиданный поворот или связь между предыдущими фактами. Герой сам замечает закономерность.' },
      { name: 'Wow-момент', percentage: 15, description: 'Самый удивительный факт — ОДИН на всю историю. Контринтуитивный, с конкретной цифрой. Герой поражён.' },
      { name: 'Проверка знаний', percentage: 15, description: 'Герой ПРИМЕНЯЕТ новые знания для решения проблемы или объяснения нового явления. Не пересказ, а действие.' },
      { name: 'Возвращение', percentage: 10, description: 'Герой возвращается, видит мир иначе. Короткий, ёмкий финал — не пересказывай факты.' },
    ],
    rules: [
      'Все факты НАУЧНО ВЕРНЫЕ и КОНКРЕТНЫЕ — называй числа, названия, термины (простым языком)',
      'Каждый факт = сцена, а не объяснение. Но внутри сцены факт НАЗВАН ЯВНО (планета Юпитер, а не "огромная планета")',
      'Сократический метод: герой задаёт ВОПРОСЫ и получает ответы с реальной информацией',
      'Аналогии через знакомое: "Как трубы в доме — так сосуды в теле"',
      '1 wow-факт на историю — контринтуитивный, с конкретными цифрами или сравнением',
      'Show, Don\'t Tell: факты через действие и наблюдение, а не через объяснение',
      'Ребёнок должен УЗНАТЬ что-то новое после прочтения — если убрать сюжет, останется 3-5 реальных фактов',
    ],
    prohibitions: [
      'Ненаучные факты, ложная информация',
      'Лекции и "урок на тему..."',
      'Сложные термины без объяснения',
      'Скучные перечисления фактов',
      'Тест или вопросы к ребёнку в конце',
      'Абстрактные "открытия" без конкретики — "огромная планета" вместо "Юпитер", "красивый камень" вместо "аметист"',
      'Сюжет без реального образовательного содержания — история ДОЛЖНА учить, а не просто развлекать на научном фоне',
      'Однообразная подача фактов — если все открытия идут по шаблону "увидел → назвали → эмоция", чередуй: диалог, эксперимент, загадка, сравнение, спор',
      'Пустые эмоциональные блоки без фактического содержания — каждый блок должен либо содержать факт, либо двигать сюжет',
    ],
    style: {
      dialogCount: '6-8',
      pacing: 'Через любопытство. Герой удивляется, задаёт вопросы. Проводник отвечает на вопросы, а не читает лекции.',
      extra: ['Визуальные описания: как выглядит то, что обычно не видно'],
    },
  },

  classic: {
    name: 'Классическая волшебная сказка',
    systemPrompt: 'Ты — хранитель древних традиций волшебных сказок. Создаёшь сказки с волшебством, тремя испытаниями, волшебными помощниками и счастливым концом. Магия помогает, но не решает всё за героя.',
    beats: [
      { name: 'Зачин', percentage: 10, description: 'Начни оригинально: действие, диалог, яркий образ. Представь героя, его мир.' },
      { name: 'Беда или недостача', percentage: 10, description: 'Что-то нарушает мир. Проблема, задание, цель.' },
      { name: 'Путь героя', percentage: 50, description: 'Три испытания, каждое тематически РАЗНОЕ (сила, ум, доброта). Волшебный помощник помогает ОДИН раз. Волшебный предмет из начала используется в кульминации. Каждое препятствие сложнее.' },
      { name: 'Кульминация', percentage: 15, description: 'Решающее испытание. Герой использует всё, чему научился. Волшебный предмет играет решающую роль.' },
      { name: 'Развязка', percentage: 15, description: 'Проблема решена. Герой возвращается изменившимся. Мораль через действия, не напрямую.' },
    ],
    rules: [
      'Волшебный помощник помогает ОДИН раз, дальше герой сам',
      'Волшебный предмет из начала — в кульминации (Чеховское ружьё)',
      'Три испытания: сила → ум → доброта',
      'Повторы для ритма: "Шёл-шёл, и пришёл..."',
      'Магия помогает, но не решает всё за героя',
      'Show, Don\'t Tell: показывай характер через действия и выбор, а не через описания',
    ],
    prohibitions: [],
    style: {
      dialogCount: '5-7',
      pacing: 'Сказочный ритм. Яркие сравнения ("глаза как звёзды"). Звукоподражания. Каждый персонаж говорит по-своему.',
      extra: ['Говорящие животные, волшебные предметы, заклинания'],
    },
  },
};

// Legacy aliases
GENRES['bedtime'] = GENRES['lullaby'];
GENRES['funny'] = GENRES['comedy'];
GENRES['serious'] = GENRES['fable'];

// ─── Age × Genre Adaptations ───────────────────────────────────────

const AGE_ADAPTATIONS: Record<string, Record<AgeGroup, string>> = {
  mystery: {
    '1-3': 'Одна простейшая загадка (кто спрятался?), 1 «подозреваемый» — знакомое животное. Ответ очевиден из звуков/картинок. Взрослый рядом, всё безопасно.',
    '3-5': 'Простая загадка (кто спрятал? что пропало?), максимум 2 подозреваемых, улики — картинки (цвет, форма, след). Герой находит ответ с помощью взрослого.',
    '5-7': 'Понятная загадка с 2-3 подозреваемыми, простые логические связи, герой рассуждает вслух простыми фразами.',
    '7-10': 'Полноценное расследование с 3 подозреваемыми, ложным следом, цепочкой улик. Герой использует наблюдательность и логику.',
    '10+': 'Сложная дедукция, 3-4 подозреваемых с мотивами, неочевидные связи между уликами, неожиданная но логичная развязка. Дедлайн.',
  },
  comedy: {
    '1-3': 'Только физический юмор: упал, измазался, чихнул. Одно смешное звукоподражание повторяется (БУХ! БУХ!). Никаких словесных шуток.',
    '3-5': 'Физический юмор (упал, запутался, измазался), громкие звукоподражания (БАМ! ПЛЮХ!), много повторов смешной фразы, которую дети кричат вместе с чтецом.',
    '5-7': 'Абсурдные ситуации (животное в человеческой роли), смешные диалоги, нелепый взрослый, эскалация хаоса.',
    '7-10': 'Игра слов, ситуационный юмор, ирония. Персонажи с комичными характерами, быстрый обмен репликами.',
    '10+': 'Самоирония, пародия на жанры, комедия характеров, сатирические ситуации. Юмор тоньше, но добрый.',
  },
  lullaby: {
    '1-3': 'Только ритм и повтор: «баю-бай, баю-бай». 1 предложение на блок, рефрен каждый блок. Образы: мама, луна, одеяльце. Никаких сюжетных поворотов.',
    '3-5': 'Минимум текста на блок, рефрен каждые 2 блока, уменьшительно-ласкательные формы (звёздочка, облачко), простейшие образы (луна, одеяло, мама).',
    '5-7': 'Более длинное "путешествие ко сну" с мягкой фантазией (облачный замок, сонный поезд), но темп замедляется к финалу.',
    '7-10': 'Созерцательная история с красивыми описаниями природы, мягкая рефлексия дня, поэтичные образы.',
    '10+': 'Медитативная история с атмосферными описаниями, внутренний монолог героя, философское спокойствие.',
  },
  adventure: {
    '1-3': '1 локация (комната, двор), мама/папа рядом. «Приключение» — найти игрушку, дойти до дерева. Никакой опасности, только радость открытия.',
    '3-5': '2 простых локации (вместо 3), добрый помощник рядом всё время, испытания — простые задачки (найти, собрать). Без по-настоящему опасных моментов.',
    '5-7': '3 яркие локации, забавный компаньон (животное), испытания на смекалку. Динамичный темп, яркие сенсорные описания.',
    '7-10': 'Полноценный "Путь героя", 3 уникальные локации, серьёзные испытания, рост персонажа. Чеховское ружьё обязательно.',
    '10+': 'Сложный квест, неоднозначные персонажи, внутренний конфликт героя наряду с внешним. Реалистичные последствия решений.',
  },
  fable: {
    '1-3': '1 животное с одной простой чертой (добрый мишка). Мораль через действие: поделился — стало весело. Без конфликта, без антагониста.',
    '3-5': '2 антропоморфных животных с одной яркой чертой каждый (жадный/щедрый), минималистичный сеттинг (поляна, пруд), мягкие последствия.',
    '5-7': 'Контраст двух подходов через параллельные сцены, мораль видна из результатов действий, тёплое примирение.',
    '7-10': 'Сложная ситуация выбора, оба персонажа вызывают сочувствие, мораль неоднозначна (оба подхода имеют плюсы).',
    '10+': 'Философская притча, сложная моральная дилемма, оба персонажа правы по-своему, финал для размышлений.',
  },
  everyday: {
    '1-3': 'Одно привычное действие (купание, еда, прогулка). Ребёнок повторяет за взрослым. Радость от простого: плюх в лужу, ложка в рот. Никаких конфликтов.',
    '3-5': 'Микроскопические ставки (кто нажмёт кнопку в лифте, сломалась башня из кубиков), трансформация рутины через игру (ковёр → лава). Диалоги обрывисто, эмоционально.',
    '5-7': 'Узнаваемые ситуации (новый друг, первый день в садике), мудрый но неидеальный родитель подыгрывает.',
    '7-10': 'Сложные эмоции (зависть, стеснение, непонимание), герой пробует справиться сам, маленькая реалистичная победа.',
    '10+': 'Подростковые переживания (идентичность, давление группы), реалистичные диалоги, нет идеальных решений, рефлексия.',
  },
  educational: {
    '1-3': '1 факт-ощущение: «корова говорит МУ», «вода мокрая». Познание через сенсорику (потрогал, послушал, понюхал). Называем предмет/животное и его главное свойство.',
    '3-5': '1-2 простых факта через яркие образы, герой "путешествует" внутрь явления (уменьшился). Аналогии через знакомые предметы. Каждый факт НАЗВАН: "это бабочка-монарх", "это планета Марс".',
    '5-7': '2-3 факта с КОНКРЕТНЫМИ названиями и числами, герой задаёт "почему?", Сократический метод, 1 wow-факт с реальной цифрой/сравнением, простые эксперименты.',
    '7-10': '3-5 фактов с логическими связями и ТОЧНЫМИ данными (названия, числа, сравнения), герой самостоятельно делает выводы, контринтуитивные факты, момент "Эврика!".',
    '10+': 'Научная концепция с глубиной, ТОЧНЫЕ термины и данные, причинно-следственные цепочки, герой применяет знания для решения проблемы.',
  },
  classic: {
    '1-3': 'Один повтор-ритуал: «тянут-потянут — вытянуть не могут». 1-2 персонажа, 1 волшебный предмет. Конец всегда счастливый и мгновенный.',
    '3-5': 'Простая структура (жили-были → беда → помощь → победа), яркие повторы ("шёл-шёл, и пришёл"), волшебный помощник.',
    '5-7': '3 испытания с нарастающей сложностью, волшебный предмет, говорящие животные, яркие сравнения.',
    '7-10': 'Полноценная структура с Чеховским ружьём, морально сложный злодей, герой растёт через испытания.',
    '10+': 'Переосмысление сказочных канонов, нетривиальная мораль, неожиданные повороты в знакомой структуре.',
  },
};

// Aliases
AGE_ADAPTATIONS['bedtime'] = AGE_ADAPTATIONS['lullaby'];
AGE_ADAPTATIONS['funny'] = AGE_ADAPTATIONS['comedy'];
AGE_ADAPTATIONS['serious'] = AGE_ADAPTATIONS['fable'];

// ─── Language Maps ──────────────────────────────────────────────────


export const AGE_DESCRIPTIONS: Record<string, string> = {
  '1-3': 'для малышей 1-3 лет (самые простые слова, 1-2 коротких предложения на блок, повторяющиеся звуки и ритм, тактильные и звуковые образы, никаких страшных моментов)',
  '3-5': 'для детей 3-5 лет (очень простой язык, короткие предложения, понятные образы)',
  '5-7': 'для детей 5-7 лет (простой язык, ясный сюжет, яркие персонажи)',
  '7-10': 'для детей 7-10 лет (развернутый сюжет, интересные повороты, проработанные детали)',
  '10+': 'для детей от 10 лет (сложный сюжет, проработанные характеры, глубокие темы)',
};

// ─── Genre Style Examples ──────────────────────────────────────────

const GENRE_STYLE_EXAMPLES: Record<string, string> = {
  adventure: `### Примеры стиля (приключение):
ХОРОШО: «Тропинка нырнула в заросли, и ветки сомкнулись над головой, как зелёный тоннель. Лис замер. Впереди, за поваленным дубом, что-то поблёскивало.»
ПЛОХО: «Они шли по тропинке. Тропинка была в зарослях. Впереди было дерево. За деревом что-то блестело.»`,

  mystery: `### Примеры стиля (детектив):
ХОРОШО: «На подоконнике — свежая царапина. Миша провёл пальцем: краска ещё не высохла. — Значит, окно открывали совсем недавно, — прошептал он.»
ПЛОХО: «Миша увидел царапину. Краска была свежая. Он понял, что окно открывали.»`,

  comedy: `### Примеры стиля (комедия):
ХОРОШО: «Кот с размаху плюхнулся в торт — БАМС! — и вынырнул с вишенкой на носу, моргая так невинно, будто торт сам на него напал.»
ПЛОХО: «Кот упал в торт. На носу была вишенка. Он выглядел невинно.»`,

  lullaby: `### Примеры стиля (колыбельная):
ХОРОШО: «Облака плыли медленно-медленно, и каждое несло на себе маленькую звёздочку, укутанную в лунный свет, как в мягкое одеяльце...»
ПЛОХО: «Облака плыли. На них были звёзды. Звёзды были укутаны.»`,

  fable: `### Примеры стиля (басня/притча):
ХОРОШО: «Муравей тащил соломинку втрое больше себя и не жаловался. Стрекоза летела налегке — зато песенка её звенела на весь луг.»
ПЛОХО: «Муравей работал. Стрекоза не работала. Она пела.»`,

  everyday: `### Примеры стиля (бытовая история):
ХОРОШО: «Маша стояла у двери садика, сжимая мамину руку так крепко, что пальцы побелели. Внутри кто-то смеялся, и смех казался слишком громким.»
ПЛОХО: «Маша стояла у садика. Она держала маму за руку. Ей было страшно.»`,

  educational: `### Примеры стиля (познавательная):
ХОРОШО (факт через сцену): «— А знаешь, почему небо голубое? — Профессор Сова склонила голову. — Солнечный луч — это целая радуга, спрятанная в одном лучике. Когда он врезается в воздух...»
ПЛОХО (лекция): «Небо голубое, потому что свет рассеивается. Это называется рэлеевское рассеяние. Короткие волны рассеиваются сильнее.»

ХОРОШО (конкретный факт): «Юпитер оказался таким огромным, что внутри него поместились бы тысяча триста Земель! Ника даже зажмурилась, представив такую гору планет.»
ПЛОХО (абстракция без факта): «Перед ними висела огромная планета. Она была очень-очень большой, больше всех остальных. Ника удивилась.»

ВАЖНО: в познавательной истории КАЖДОЕ "Открытие" (beat) должно содержать КОНКРЕТНЫЙ научный факт с названием, числом или сравнением. Абстрактные описания без фактического содержания = провал жанра.`,

  classic: `### Примеры стиля (классическая сказка):
ХОРОШО: «Шёл-шёл Иванушка, и пришёл к озеру, гладкому как зеркало. На берегу сидела лягушка — да не простая, а в крошечной золотой короне.»
ПЛОХО: «Иванушка пришёл к озеру. Озеро было гладкое. Там была лягушка. У лягушки была корона.»`,
};

// Aliases
GENRE_STYLE_EXAMPLES['bedtime'] = GENRE_STYLE_EXAMPLES['lullaby'];
GENRE_STYLE_EXAMPLES['funny'] = GENRE_STYLE_EXAMPLES['comedy'];
GENRE_STYLE_EXAMPLES['serious'] = GENRE_STYLE_EXAMPLES['fable'];

// ─── Public API ─────────────────────────────────────────────────────
